// ============================================================================
// RatiosSection — body ratios (WHR / WHtR) shown against their healthy risk
// thresholds via an animated gauge, plus the ratio trend over time. Each ratio
// renders only when present in `ratios.latest`.
// ============================================================================
import { useTranslation } from "react-i18next";
import { Badge } from "../../../components";
import { ThresholdGauge } from "../charts/ThresholdGauge";
import { LineChart } from "../charts/LineChart";
import { ReportSection } from "./ReportSection";
import type { RatioReading, TypedClientReport } from "../types";
import { fmtNum } from "../format";

function RatioRow({
  label,
  reading,
  locale,
}: {
  label: string;
  reading: RatioReading;
  locale: string;
}) {
  const { t } = useTranslation("clientReport");
  const within = reading.value <= reading.threshold;

  return (
    <div className="rounded-lg bg-surface-soft p-4 ring-1 ring-line-soft">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-sm font-bold text-body">{label}</span>
          <span className="nums font-display text-lg font-extrabold tracking-tightest text-body">
            {fmtNum(reading.value, 2, locale)}
          </span>
        </div>
        <Badge tone={within ? "success" : "warning"} solid={!within}>
          {within ? t("ratios.atOrBelow") : t("ratios.above")}
        </Badge>
      </div>
      <ThresholdGauge
        value={reading.value}
        threshold={reading.threshold}
        highThreshold={reading.high_threshold}
        withinRange={within}
        lowLabel={t("ratios.scaleLow")}
        highLabel={t("ratios.scaleHigh")}
        ariaLabel={`${label} ${fmtNum(reading.value, 2, locale)}`}
        locale={locale}
      />
      <p className="mt-2 text-xs text-body-faint">
        {t("ratios.threshold")} {fmtNum(reading.threshold, 2, locale)}
        {reading.high_threshold !== undefined &&
          ` · ${fmtNum(reading.high_threshold, 2, locale)}`}
      </p>
    </div>
  );
}

export function RatiosSection({
  report,
  index,
}: {
  report: TypedClientReport;
  index: number;
}) {
  const { t, i18n } = useTranslation("clientReport");
  const locale = i18n.language;
  const { ratios, body } = report;
  const { whr, whtr } = ratios.latest;

  // Prefer the WHR trend, fall back to WHtR — matches the backend chart choice.
  // Keep the threshold reference line aligned with the series actually shown.
  const useWhr = (body.series.whr?.length ?? 0) >= 2;
  const useWhtr = !useWhr && (body.series.whtr?.length ?? 0) >= 2;
  const trendSeries = useWhr ? body.series.whr : useWhtr ? body.series.whtr : null;
  const trendThreshold = useWhr
    ? whr?.threshold
    : useWhtr
      ? whtr?.threshold
      : undefined;

  return (
    <ReportSection
      id="report-ratios"
      index={index}
      kicker={t("kicker")}
      title={t("ratios.title")}
      subtitle={t("ratios.subtitle")}
      hasData={ratios.ok}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {whr && (
          <RatioRow label={t("metrics.whr")} reading={whr} locale={locale} />
        )}
        {whtr && (
          <RatioRow label={t("metrics.whtr")} reading={whtr} locale={locale} />
        )}
      </div>

      {trendSeries && (
        <div className="mt-6">
          <h3 className="mb-2 font-display text-sm font-bold text-body-soft">
            {t("ratios.trend")}
          </h3>
          <LineChart
            series={trendSeries}
            accent="lime"
            decimals={2}
            threshold={trendThreshold}
            thresholdLabel={t("ratios.threshold")}
            ariaLabel={t("ratios.trend")}
            locale={locale}
          />
        </div>
      )}
    </ReportSection>
  );
}
